import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "Utils";
import {
  useTypesenseProxyContext,
  ProxySearchParams,
} from "contexts/TypesenseProxyContext";
import { SurveyTypesenseDocument } from "@max/common/setfan";

export const useArtistSurveys = ({
  groupId = "",
  page = 1,
  query = "*",
  queryBy = "title,internalTitle",
  filterBy = "",
  sortBy = "",
  resultsPerPage = 5,
} = {}) => {
  const [documents, setDocuments] = useState<SurveyTypesenseDocument[]>([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [totalArtistCampaigns, setTotalArtistCampaigns] = useState(0);
  const [loadingQuery, setLoadingQuery] = useState(false);
  const [loadingTotal, setLoadingTotal] = useState(false);

  const { proxySearch } = useTypesenseProxyContext();

  const searchParameters: ProxySearchParams = useMemo(
    () => ({
      q: query,
      query_by: queryBy,
      sort_by: sortBy,
      filter_by: filterBy,
      page: page,
      per_page: resultsPerPage,
    }),
    [query, page, resultsPerPage, filterBy, queryBy, sortBy],
  );

  async function getDocuments(searchParameters: ProxySearchParams) {
    proxySearch("surveys", searchParameters)
      .then((resp) => {
        setTotalDocuments(resp?.found ?? 0);
        setDocuments(resp.hits?.map((survey) => survey.document) ?? []);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingQuery(false);
      });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedQuery = useCallback(debounce(getDocuments), []);

  function forceReload() {
    if (proxySearch) getDocuments(searchParameters);
  }

  useEffect(() => {
    if (proxySearch) {
      setLoadingQuery(true);
      debouncedQuery(searchParameters);
    }
  }, [proxySearch, searchParameters, debouncedQuery]);

  useEffect(() => {
    // total surveys count for artist
    if (proxySearch && groupId) {
      setLoadingTotal(true);
      proxySearch("surveys", {
        q: "*",
        filter_by: `artistGroupId:=${groupId}`,
      })
        .then((resp) => {
          setTotalArtistCampaigns(resp.found ?? 0);
        })
        .catch(console.error)
        .finally(() => setLoadingTotal(false));
    }
  }, [groupId, proxySearch]);

  return {
    documents,
    loading: loadingQuery || loadingTotal,
    forceReload,
    totalDocuments,
    totalArtistCampaigns,
  };
};
